import { action, observable, runInAction } from "mobx";
import { RootStore } from ".";
import { fetchWrapper } from "../common/Fetch";
import { PagedList } from "./CommonStore";
import { NotificationLevel } from "./NotificationStore";
import { SuccessableResult, KeyValue } from "../common/BaseClasses";
import { formatDateJson, formatMoney } from "../common/Formatting";

export interface CustomerOrder {
    isConfirmed: boolean;
    managerName: string;
    clientName: string;
    clientPhone: string;
    deliveryPointName: string;
    managerComment: string;
    clientComment: string;
    statusName: string;
    orderNumber: string;
    orderGUID: string;
    dateCreate: Date;
    orderSum: number;
    source: string;
    sourceName: string;
    positionsCount: number;
    inWorkPositionsCount: number;
    completedPositionsCount: number;
    rejectedPositionsCount: number;
    awaitingPositionsCount: number;
    isHidden: boolean;
}
export interface DraftOrder {
    managerName: string;
    customerComment: string;
    comment: string;
    commentForClient: string;
    statusName: string;
    id: number;
    number: string;
    customerId: number;
    customerName: string;
    dateCreate: Date;
    sum: number;
    positionCount: number;
    deliveryPointId?: number;
}

export interface CustomerOrderShortDetails {
    source: string;
    orderSum: number;
    deliveryPointName: string;
    deliveryPointId?: number;
    orderNumber: number;
    orderGuid: string;
    customerId: number;
    customerName: string;
    customerPhone: string;
    dateCreate: Date;
    positions: CustomerOrderShortPosition[];
    payments: CustomerOrderPayment[];
}

export interface CustomerOrderDetails extends CustomerOrderShortDetails {
    confirmationDate?: Date;
    confirmationManager?: string;
    supplierName: string;
    payTypeName: string;
    deliveryTypeName: string;
    customerComment: string;
    managerComment: string;
    statusId: number;
    statusName: string;
    typePayId: number;
    typePayName: string;
    positions: CustomerOrderPosition[];
    autocreatedOrders: AutocreatedOrder[];
    parentOrderId?: number;
    parentOrderGuid?: string;
    isHidden: boolean;
    paymentLink: string;
}

export interface CustomerOrderShortPosition {
    isChecked: boolean | undefined;
    id: number;
    articleId: string;
    brand: string;
    article: string;
    description: string;
    rejectCount: number;
    rejectDate: Date;
    rejectInfo: string;
    count: number;
    cost: number;
    sum: number;
    statusName: string;
    statusColor: string;
    statusCode: number;
    statusCategoryId: number;
}

export interface CustomerOrderPosition extends CustomerOrderShortPosition {
    supName: string;
    supCode: string;
    deliveryTimeString: string;
    guaranteeDateString: string;
    awaitedDateString: string;
}

export interface CustomerOrderPositionReturn extends CustomerOrderPosition {
    returnCount: number;
}
export interface CustomerOrderPayment {
    dateCreate: Date;
    sum: number;
    isCache: boolean;
    isIncoming: boolean;
    paymentTypeName: string;
    paymentTypeId: number;
}
export interface AutocreatedOrder {
    orderId: number;
    orderGuid: string;
}

interface ConfirmationOrderInfo {
    confirmationDate: Date;
    confirmationManager: string;
}

export interface PositionStatusHistory {
    positionId: number;
    dateAdded: Date;
    color: string;
    name: string;
}

export class CustomerOrdersStore {



    _rootStore: RootStore;

    constructor(rootStore: RootStore) {
        this._rootStore = rootStore;
    }
    @observable unacceptedOrdersCount = 0;

    @observable currentPage = 1;
    @observable totalOrdersCount = 1;

    @observable currentPageDraftOrders = 1;
    @observable totalDraftOrdersCount = 1;

    @observable orders: CustomerOrder[] = [];
    @observable draftOrders: DraftOrder[] = [];

    @observable managers: KeyValue<string, string>[] = [];
    @observable statuses: KeyValue<number, string>[] = [];


    @action
    loadOrders(managerIds: string[], customerId: string, page = 1, onlyUnAccepted: boolean, showHidden: boolean, source?: string, dateFrom?: Date, dateTo?: Date, searchString?: string) {
        this.orders = [];
        const managerIdsParam = managerIds.length <= 0 ? "" : "&managerIds=" + managerIds.join("&managerIds=");
        customerId = customerId == "-1" ? "" : customerId;
        const dateFromStr = dateFrom == undefined ? '' : formatDateJson(dateFrom);
        const dateToStr = dateTo == undefined ? '' : formatDateJson(dateTo);
        source = source || "";

        fetchWrapper(`api/customerorders/list/${page}?customerId=${customerId}${managerIdsParam}&source=${source}&dateFrom=${dateFromStr}&dateTo=${dateToStr}&onlyUnAccepted=${onlyUnAccepted}&showHidden=${showHidden}&searchString=${searchString || ''}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        }, this._rootStore.commonStore)
            .then(res => res.json() as Promise<PagedList<CustomerOrder>>)
            .then(result => {
                runInAction("fetch complete loadOrders", () => {
                    this.currentPage = page;
                    this.totalOrdersCount = result.totalItemCount;
                    this.orders = result.items;
                });
            })
            .catch(res => {
                console.log(res);
            });
    }
    @action
    loadManagers() {
        this.orders = [];
        fetchWrapper(`api/users/GetCallCenterManagers`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        }, this._rootStore.commonStore)
            .then(res => res.json() as Promise<KeyValue<string, string>[]>)
            .then(result => {
                runInAction("fetch complete loadManagers", () => {
                    this.managers = result;
                });
            })
            .catch(res => {
                console.log(res);
            });
    }
    @action
    loadStatuses() {
        this.orders = [];
        fetchWrapper(`api/customerorders/getstatuses`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        }, this._rootStore.commonStore)
            .then(res => res.json() as Promise<KeyValue<number, string>[]>)
            .then(result => {
                runInAction("fetch complete loadManagers", () => {
                    this.statuses = result;
                });
            })
            .catch(res => {
                console.log(res);
            });
    }
    @action
    loadDraftOrders(managerIds: string[], customerId: string, source?: string, page = 1, dateFrom?: Date, dateTo?: Date, searchString?: string) {
        this.draftOrders = [];
        const managerIdsParam = managerIds.length <= 0 ? "" : "&managerIds=" + managerIds.join("&managerIds=");
        const dateFromStr = dateFrom == undefined ? '' : formatDateJson(dateFrom);
        const dateToStr = dateTo == undefined ? '' : formatDateJson(dateTo);
        fetchWrapper(`api/customerorders/draftlist/${page}?customerId=${customerId}${managerIdsParam}&dateFrom=${dateFromStr}&dateTo=${dateToStr}&searchString=${searchString || ''}&source=${source}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        }, this._rootStore.commonStore)
            .then(res => res.json() as Promise<PagedList<DraftOrder>>)
            .then(result => {
                runInAction("fetch complete loadOrders", () => {
                    this.currentPageDraftOrders = page;
                    this.totalDraftOrdersCount = result.totalItemCount;
                    this.draftOrders = result.items;
                });
            })
            .catch(res => {
                console.log(res);
            });
    }

    @action
    removeDraftOrder(id: number) {
        fetchWrapper(`api/customerorders/removedraftorder/${id}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json'
            }
        }, this._rootStore.commonStore)
            .then(res => res.json() as Promise<SuccessableResult<any>>)
            .then(result => {
                runInAction("fetch complete removeDraftOrder", () => {
                    if (result.isSuccess) {

                        this._rootStore.notificationStore.addNotification("Коммерческое предложение удалено!", NotificationLevel.success);
                        this.draftOrders = this.draftOrders.filter(w => w.id != id);
                    }
                    else
                        this._rootStore.notificationStore.addNotification("Ошибка при удалении коммерческого предложения! " + result.errorMessage, NotificationLevel.error);
                });
            })
            .catch(res => {
                console.log(res);
            });
    }

    @action
    loadOrder(orderGuid: string, successCallback: (order: CustomerOrderDetails) => void) {
        fetchWrapper(`api/customerorders/details/${orderGuid}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        }, this._rootStore.commonStore)
            .then(res => res.json() as Promise<CustomerOrderDetails>)
            .then(result => {
                runInAction("fetch complete loadOrders", () => {
                    successCallback(result);
                });
            })
            .catch(res => {
                console.log(res);
            });
    }

    @action
    loadOrderShort(orderGuid: string, successCallback: (shortOrder: CustomerOrderShortDetails) => void) {
        fetchWrapper(`api/customerorders/shortdetails/${orderGuid}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        }, this._rootStore.commonStore)
            .then(res => res.json() as Promise<CustomerOrderShortDetails>)
            .then(result => {
                runInAction("fetch complete loadOrderShort", () => {
                    successCallback(result);
                });
            })
            .catch(res => {
                console.log(res);
            });
    }
    @action
    loadStatusesHistory(positionId: number, successCallback: (historyItems: PositionStatusHistory[]) => void) {
        fetchWrapper(`api/customerorders/details/statushistory/${positionId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        }, this._rootStore.commonStore)
            .then(res => res.json() as Promise<PositionStatusHistory[]>)
            .then(result => {
                runInAction("fetch complete loadStatusesHistory", () => {
                    successCallback(result);
                });
            })
            .catch(res => {
                console.log(res);
            });
    }


    @action
    saveConfirmation(orderGuid: string, successCallback: (confirmationDate: Date, confirmationManager: string) => void) {
        fetchWrapper(`api/CustomerOrders/confirmorder/${orderGuid}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            }
        }, this._rootStore.commonStore)
            .then(res => res.json() as Promise<SuccessableResult<ConfirmationOrderInfo>>)
            .then(result => {
                runInAction("fetch complete saveConfirmation", () => {
                    if (result.isSuccess) {
                        this._rootStore.notificationStore.addNotification("Заказ подтвержден!", NotificationLevel.success);
                        successCallback(result.data.confirmationDate, result.data.confirmationManager);
                    }
                    else {
                        this._rootStore.notificationStore.addNotification("Ошибка при сохранении подтверждения! " + result.errorMessage, NotificationLevel.error);
                    }

                });
            })
            .catch(res => {
                console.log(res);
            });
    }

    @action
    toggleHiddenStatus(orderGuid: string, successCallback: (isHidden: boolean) => void) {
        fetchWrapper(`api/CustomerOrders/toggleorder/${orderGuid}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            }
        }, this._rootStore.commonStore)
            .then(res => res.json() as Promise<SuccessableResult<boolean>>)
            .then(result => {
                runInAction("fetch complete toggleHiddenStatus", () => {
                    if (result.isSuccess) {
                        this._rootStore.notificationStore.addNotification(`Заказ ${result.data ? "скрыт" : "показан"}!`, NotificationLevel.success);
                        successCallback(result.data);
                    }
                    else {
                        this._rootStore.notificationStore.addNotification("Ошибка при изменении статуса отображения заказ! " + result.errorMessage, NotificationLevel.error);
                    }

                });
            })
            .catch(res => {
                console.log(res);
            });
    }


    @action
    loadUnacceptedOrdersCount() {
        if (this._rootStore.authStore.isAuthenticated && (this._rootStore.authStore.isCallCentre))
            fetchWrapper(`api/CustomerOrders/getunacceptedorderscount`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            }, this._rootStore.commonStore, false)
                .then(res => res.json() as Promise<number>)
                .then(result => {
                    runInAction("fetch complete loadUnacceptedOrdersCount", () => {
                        this.unacceptedOrdersCount = result;
                    });
                })
                .catch(res => {
                    console.log(res);
                });
    }


    @action
    addCacheInPaymentByOrder(orderId: number, sum: number, successCallback: () => void) {
        fetchWrapper(`api/Payments/AddPayment/${orderId}/${sum}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            }
        }, this._rootStore.commonStore)
            .then(res => res.json() as Promise<SuccessableResult<any>>)
            .then(result => {
                runInAction("fetch complete addCacheInPaymentByOrder", () => {
                    if (result.isSuccess) {
                        this._rootStore.notificationStore.addNotification(`Платеж по заказу ${orderId} создан!`, NotificationLevel.success);
                        successCallback();
                    }
                    else {
                        this._rootStore.notificationStore.addNotification("Ошибка при создании платежа! " + result.errorMessage, NotificationLevel.error);
                    }

                });
            })
            .catch(res => {
                console.log(res);
            });
    }


    @action
    printCheque(sum: number, orderNumber: number) {
        fetchWrapper(`api/Payments/printcheque/${orderNumber}/${sum}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            }
        }, this._rootStore.commonStore)
            .then(res => res.json() as Promise<SuccessableResult<any>>)
            .then(result => {
                runInAction("fetch complete printCheque", () => {
                    if (result.isSuccess) {
                        this._rootStore.notificationStore.addNotification(`Чек по заказу ${orderNumber} на сумму ${formatMoney(sum)}руб. создан!`, NotificationLevel.success);
                    }
                    else {
                        this._rootStore.notificationStore.addNotification("Ошибка при печати чека! " + result.errorMessage, NotificationLevel.error);
                    }

                });
            })
            .catch(res => {
                console.log(res);
            });
    }

}
